"use client";

import { motion, type MotionValue, useTransform } from "motion/react";

import {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATHS,
} from "@/components/shared/logo";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

import { useFooterProgress } from "./footer-reveal";

// =============================================================================

type StrokeProps = {
  d: string;
  progress: MotionValue<number>;
  range: [number, number];
  stroke?: string;
};

function Stroke({ d, progress, range, stroke }: StrokeProps) {
  const pathLength = useTransform(progress, range, [0, 1], { clamp: true });
  const opacity = useTransform(progress, [range[0], range[0] + 0.01], [0, 1]);
  return <motion.path d={d} stroke={stroke} style={{ pathLength, opacity }} />;
}

// =============================================================================

/**
 * Giant "b" of the footer, drawn while the footer is uncovered. Same
 * geometry as the logo; the bowl is centered on the page as in the design.
 */
export function FooterWatermark({ className }: { className?: string }) {
  const progress = useFooterProgress();
  const shouldReduceMotion = usePrefersReducedMotion();
  const drawn = useTransform(progress, () => 1);
  const source = shouldReduceMotion ? drawn : progress;

  const scale = useTransform(progress, [0, 1], [0.86, 1]);
  const y = useTransform(progress, [0, 1], ["-8%", "0%"]);

  return (
    <motion.div
      aria-hidden
      className={cn("pointer-events-none", className)}
      style={shouldReduceMotion ? undefined : { scale, y }}
    >
      <svg
        viewBox="148 -40 217 457"
        fill="none"
        strokeLinecap="round"
        className="h-full w-auto max-w-[88vw] text-foreground opacity-[0.08]"
      >
        <defs>
          <linearGradient
            id="footer-bowl"
            x1="256.426"
            y1="211.165"
            x2="351.413"
            y2="293.456"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
          <linearGradient
            id="footer-inner"
            x1="297.59"
            y1="347.798"
            x2="217.085"
            y2="347.881"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
        </defs>
        <g stroke="currentColor" strokeWidth={LOGO_OUTER_STROKE}>
          {/* The stem comes from above the footer */}
          <Stroke
            d={LOGO_PATHS.stemAndBowl.replace("M159 72", "M159 -40")}
            progress={source}
            range={[0.05, 0.62]}
          />
          <Stroke
            d={LOGO_PATHS.bowlTop}
            stroke="url(#footer-bowl)"
            progress={source}
            range={[0.55, 0.85]}
          />
        </g>
        <g stroke="currentColor" strokeWidth={LOGO_INNER_STROKE}>
          <Stroke
            d={LOGO_PATHS.innerTop}
            progress={source}
            range={[0.4, 0.75]}
          />
          <Stroke
            d={LOGO_PATHS.innerBottom}
            stroke="url(#footer-inner)"
            progress={source}
            range={[0.65, 0.95]}
          />
        </g>
      </svg>
    </motion.div>
  );
}

// =============================================================================
